<script setup>
import { computed, reactive, ref, watch } from 'vue'
import ModalDialog from './ModalDialog.vue'
import BookingHistoryPanel from './BookingHistoryPanel.vue'
import BookingSalesPanel from './BookingSalesPanel.vue'
import { bookingMoneyCents, bookingPriceChanged } from '../api/booking-history'
import { options, resourceTypeLabels } from '../api/mappers'
import { toApiDateTime, toDateTimeInput } from '../utils'

const props = defineProps({
  open: Boolean,
  booking: { type: Object, default: null },
  resources: { type: Array, default: () => [] },
  saving: Boolean,
  initialResourceId: { type: [Number, String], default: null },
  initialStartsAt: { type: String, default: '' },
  initialEndsAt: { type: String, default: '' },
  initialInquiry: { type: Object, default: null },
})
const emit = defineEmits(['close', 'save'])

const form = reactive({
  resourceId: '',
  guestName: '',
  guestPhone: '',
  guestEmail: '',
  guestsCount: 1,
  startsAt: '',
  endsAt: '',
  status: 'confirmed',
  notes: '',
  totalAmount: '',
  prepaidAmount: '',
  changeReason: '',
})
const validationError = ref('')
const historyRefresh = ref(0)
const priceChanged = computed(() => bookingPriceChanged(form, props.booking))

const selectableResources = computed(() => props.resources.filter((resource) => (
  props.initialInquiry
    ? resource.isActive && String(resource.id) === String(props.initialInquiry.resourceId)
    : resource.isActive || resource.id === props.booking?.resourceId
)))

const selectedResource = computed(() => props.resources.find((resource) => String(resource.id) === String(form.resourceId)))

function defaultDates() {
  const requestedStart = props.initialInquiry?.startsAt || props.initialStartsAt
  const requestedEnd = props.initialInquiry?.endsAt || props.initialEndsAt
  let start = requestedStart
    ? toDateTimeInput(requestedStart)
    : toDateTimeInput(new Date())
  if (!requestedStart) {
    const rounded = new Date(`${start}:00Z`)
    rounded.setUTCMinutes(0, 0, 0)
    rounded.setUTCHours(rounded.getUTCHours() + 1)
    start = rounded.toISOString().slice(0, 16)
  }
  const end = new Date(`${start}:00Z`)
  end.setUTCHours(end.getUTCHours() + 2)
  return { start, end: requestedEnd ? toDateTimeInput(requestedEnd) : end.toISOString().slice(0, 16) }
}

function reset() {
  validationError.value = ''
  const dates = defaultDates()
  const inquiry = props.initialInquiry
  Object.assign(form, {
    resourceId: props.booking?.resourceId ?? inquiry?.resourceId ?? props.initialResourceId ?? selectableResources.value[0]?.id ?? '',
    guestName: props.booking?.guestName ?? inquiry?.guestName ?? '',
    guestPhone: props.booking?.guestPhone ?? inquiry?.guestPhone ?? '',
    guestEmail: props.booking?.guestEmail ?? inquiry?.guestEmail ?? '',
    guestsCount: props.booking?.guestsCount ?? inquiry?.guestsCount ?? 1,
    startsAt: props.booking?.startsAt ? toDateTimeInput(props.booking.startsAt) : dates.start,
    endsAt: props.booking?.endsAt
      ? toDateTimeInput(props.booking.endsAt)
      : inquiry?.endsAt ? toDateTimeInput(inquiry.endsAt) : dates.end,
    status: props.booking?.status ?? 'confirmed',
    notes: props.booking?.notes ?? (inquiry?.comment ? `Заявка №${inquiry.id}: ${inquiry.comment}`.slice(0, 1000) : ''),
    totalAmount: props.booking?.totalAmount ?? '',
    prepaidAmount: props.booking?.prepaidAmount ?? '',
    changeReason: '',
  })
  historyRefresh.value = 0
}

watch(() => props.open, (open) => {
  if (open) reset()
})

function submit() {
  validationError.value = ''
  if (!form.resourceId || !form.guestName.trim() || !form.startsAt || !form.endsAt) {
    validationError.value = 'Заполните гостя, объект и даты бронирования.'
    return
  }
  if (form.endsAt <= form.startsAt) {
    validationError.value = 'Окончание должно быть позже начала бронирования.'
    return
  }
  if (Number(form.guestsCount) < 1) {
    validationError.value = 'Укажите хотя бы одного гостя.'
    return
  }
  if (selectedResource.value?.capacity && Number(form.guestsCount) > selectedResource.value.capacity) {
    validationError.value = `Вместимость объекта — ${selectedResource.value.capacity}. Уменьшите число гостей или выберите другой объект.`
    return
  }
  if (Number(form.prepaidAmount || 0) > Number(form.totalAmount || 0) && form.totalAmount !== '') {
    validationError.value = 'Предоплата не может быть больше полной стоимости.'
    return
  }
  if (['totalAmount', 'prepaidAmount'].some((field) => form[field] !== '' && bookingMoneyCents(form[field]) == null)) {
    validationError.value = 'Укажите неотрицательную сумму с точностью до копеек, не более 9 999 999 999,99 ₽.'
    return
  }
  if (priceChanged.value && !form.changeReason.trim()) {
    validationError.value = 'Укажите причину изменения стоимости или предоплаты — она останется в истории.'
    return
  }

  emit('save', {
    ...form,
    startsAt: toApiDateTime(form.startsAt),
    endsAt: toApiDateTime(form.endsAt),
  })
}
</script>

<template>
  <ModalDialog
    :open="open"
    :title="booking ? 'Редактировать бронь' : initialInquiry ? 'Бронь по заявке' : 'Новая бронь'"
    :eyebrow="booking ? `Бронирование №${booking.id}` : initialInquiry ? `Заявка №${initialInquiry.id}` : 'Бронирование'"
    wide
    @close="!saving && emit('close')"
  >
    <form class="dialog-form" @submit.prevent="submit">
      <div class="form-section">
        <h3>Гость</h3>
        <div class="form-grid form-grid--3">
          <label class="field field--span-2">
            <span>Имя гостя <b aria-hidden="true">*</b></span>
            <input v-model="form.guestName" type="text" autocomplete="name" maxlength="160" required autofocus placeholder="Иван Петров" />
          </label>
          <label class="field">
            <span>Количество гостей <b aria-hidden="true">*</b></span>
            <input v-model.number="form.guestsCount" type="number" min="1" :max="selectedResource?.capacity || 999" required />
          </label>
          <label class="field">
            <span>Телефон</span>
            <input v-model="form.guestPhone" type="tel" autocomplete="tel" maxlength="40" placeholder="+7 900 000-00-00" />
          </label>
          <label class="field field--span-2">
            <span>Электронная почта</span>
            <input v-model="form.guestEmail" type="email" autocomplete="email" maxlength="160" placeholder="guest@example.ru" />
          </label>
        </div>
      </div>

      <div class="form-section">
        <h3>Размещение</h3>
        <div class="form-grid form-grid--2">
          <label class="field field--span-2">
            <span>Объект <b aria-hidden="true">*</b></span>
            <select v-model="form.resourceId" :disabled="Boolean(initialInquiry)" required>
              <option disabled value="">Выберите объект</option>
              <option v-for="resource in selectableResources" :key="resource.id" :value="resource.id">
                {{ resource.name }} · {{ resourceTypeLabels[resource.resourceType] }} · до {{ resource.capacity }} чел.
              </option>
            </select>
          </label>
          <label class="field">
            <span>Начало <b aria-hidden="true">*</b></span>
            <input v-model="form.startsAt" type="datetime-local" required />
          </label>
          <label class="field">
            <span>Окончание <b aria-hidden="true">*</b></span>
            <input v-model="form.endsAt" type="datetime-local" required />
          </label>
          <label class="field">
            <span>Статус</span>
            <select v-model="form.status">
              <option v-for="status in options.bookingStatuses" :key="status.value" :value="status.value">
                {{ status.label }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div class="form-section">
        <h3>Оплата и комментарий</h3>
        <div class="form-grid form-grid--2">
          <label class="field">
            <span>Стоимость размещения, ₽</span>
            <input v-model="form.totalAmount" type="number" min="0" max="9999999999.99" step="0.01" inputmode="decimal" placeholder="0" />
          </label>
          <label class="field">
            <span>Предоплата за размещение, ₽</span>
            <input v-model="form.prepaidAmount" type="number" min="0" max="9999999999.99" step="0.01" inputmode="decimal" placeholder="0" />
          </label>
          <label v-if="booking" class="field field--span-2">
            <span>Причина изменения цены <b v-if="priceChanged" aria-hidden="true">*</b></span>
            <textarea v-model="form.changeReason" :required="priceChanged" rows="2" maxlength="1000" placeholder="Например, скидка по согласованию или уточнение стоимости" />
            <small>При изменении стоимости или предоплаты причина обязательна. Старая сумма, новая сумма и автор сохраняются в истории.</small>
          </label>
          <label class="field field--span-2">
            <span>Комментарий</span>
            <textarea v-model="form.notes" rows="3" maxlength="1000" placeholder="Пожелания гостя, детали заезда или оплаты" />
          </label>
        </div>
      </div>

      <p v-if="validationError" class="form-error" role="alert">{{ validationError }}</p>

      <footer class="modal-actions">
        <button type="button" class="button button--ghost" :disabled="saving" @click="emit('close')">Отмена</button>
        <button type="submit" class="button button--primary" :disabled="saving">
          <span v-if="saving" class="button-spinner" aria-hidden="true" />
          {{ saving ? 'Сохраняем…' : booking ? 'Сохранить изменения' : 'Создать бронь' }}
        </button>
      </footer>
    </form>
    <template v-if="booking">
      <BookingSalesPanel :key="`sales-${booking.id}`" :booking-id="booking.id" :booking-status="booking.status" :lodging-amount="form.totalAmount === '' ? booking.totalAmount : form.totalAmount" @changed="historyRefresh++" />
      <BookingHistoryPanel :key="`history-${booking.id}`" :booking-id="booking.id" :refresh-key="historyRefresh" />
    </template>
    <p v-else class="booking-extra-hint">После создания брони откройте её для добавления услуг, товаров и просмотра истории изменений.</p>
  </ModalDialog>
</template>

<style scoped>
.booking-extra-hint { margin-top: 18px; font-size: .78rem; color: var(--muted); }
.field small { font-size: .74rem; color: var(--muted); line-height: 1.5; }
</style>
